export const SHIFT_CALENDAR_MIN_YEAR = 1900
export const SHIFT_CALENDAR_MAX_YEAR = 2100
export const SHIFT_CALENDAR_MIN_CYCLE_DAYS = 1
export const SHIFT_CALENDAR_MAX_CYCLE_DAYS = 31
export const SHIFT_CALENDAR_MIN_HOURS = 1
export const SHIFT_CALENDAR_MAX_HOURS = 24

export type ShiftPreset = '2_2' | '3_3' | '1_3' | 'custom'

export interface ShiftCalendarInput {
  preset: ShiftPreset
  firstWorkDate: string
  year: number
  customWorkDays: number
  customRestDays: number
  shiftHours: number
}

export type ShiftCalendarError =
  | 'invalid_first_work_date'
  | 'unsupported_year'
  | 'invalid_cycle'
  | 'invalid_shift_hours'

export interface ShiftCalendarDay {
  date: string
  day: number
  month: number
  weekday: number
  isWorkDay: boolean
}

export interface ShiftCalendarResult {
  days: readonly ShiftCalendarDay[]
  workShifts: number
  restDays: number
  totalHours: number
  workDaysInCycle: number
  restDaysInCycle: number
}

export type ShiftCalendarCalculation =
  | { result: ShiftCalendarResult; error: null }
  | { result: null; error: ShiftCalendarError }

const PRESET_CYCLES: Record<Exclude<ShiftPreset, 'custom'>, readonly [number, number]> = {
  '2_2': [2, 2],
  '3_3': [3, 3],
  '1_3': [1, 3],
}

export function calculateShiftCalendar(input: ShiftCalendarInput): ShiftCalendarCalculation {
  if (
    !Number.isInteger(input.year)
    || input.year < SHIFT_CALENDAR_MIN_YEAR
    || input.year > SHIFT_CALENDAR_MAX_YEAR
  ) {
    return { result: null, error: 'unsupported_year' }
  }

  const anchor = parsePlainDate(input.firstWorkDate)
  if (!anchor) return { result: null, error: 'invalid_first_work_date' }
  if (anchor.year < SHIFT_CALENDAR_MIN_YEAR || anchor.year > SHIFT_CALENDAR_MAX_YEAR) {
    return { result: null, error: 'invalid_first_work_date' }
  }

  const cycle = resolveCycle(input)
  if (!cycle) return { result: null, error: 'invalid_cycle' }

  if (
    !Number.isInteger(input.shiftHours)
    || input.shiftHours < SHIFT_CALENDAR_MIN_HOURS
    || input.shiftHours > SHIFT_CALENDAR_MAX_HOURS
  ) {
    return { result: null, error: 'invalid_shift_hours' }
  }

  const [workDaysInCycle, restDaysInCycle] = cycle
  const cycleLength = workDaysInCycle + restDaysInCycle
  const startSerial = toDaySerial(input.year, 1, 1)
  const endSerial = toDaySerial(input.year + 1, 1, 1)
  const anchorSerial = toDaySerial(anchor.year, anchor.month, anchor.day)
  const days: ShiftCalendarDay[] = []
  let workShifts = 0

  for (let serial = startSerial; serial < endSerial; serial += 1) {
    const cycleDay = positiveModulo(serial - anchorSerial, cycleLength)
    const isWorkDay = cycleDay < workDaysInCycle
    const date = fromDaySerial(serial)
    if (isWorkDay) workShifts += 1
    days.push({
      date: formatPlainDate(date.year, date.month, date.day),
      day: date.day,
      month: date.month,
      weekday: date.weekday,
      isWorkDay,
    })
  }

  return {
    result: {
      days,
      workShifts,
      restDays: days.length - workShifts,
      totalHours: workShifts * input.shiftHours,
      workDaysInCycle,
      restDaysInCycle,
    },
    error: null,
  }
}

function resolveCycle(input: ShiftCalendarInput): readonly [number, number] | null {
  if (input.preset !== 'custom') return PRESET_CYCLES[input.preset]
  if (
    !Number.isInteger(input.customWorkDays)
    || !Number.isInteger(input.customRestDays)
    || input.customWorkDays < SHIFT_CALENDAR_MIN_CYCLE_DAYS
    || input.customWorkDays > SHIFT_CALENDAR_MAX_CYCLE_DAYS
    || input.customRestDays < SHIFT_CALENDAR_MIN_CYCLE_DAYS
    || input.customRestDays > SHIFT_CALENDAR_MAX_CYCLE_DAYS
  ) {
    return null
  }
  return [input.customWorkDays, input.customRestDays]
}

function parsePlainDate(value: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const serial = toDaySerial(year, month, day)
  const roundTrip = fromDaySerial(serial)
  if (roundTrip.year !== year || roundTrip.month !== month || roundTrip.day !== day) return null
  return { year, month, day }
}

function toDaySerial(year: number, month: number, day: number): number {
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000)
}

function fromDaySerial(serial: number): {
  year: number
  month: number
  day: number
  weekday: number
} {
  const date = new Date(serial * 86_400_000)
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    weekday: date.getUTCDay(),
  }
}

function formatPlainDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function positiveModulo(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus
}
